import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Input, Button, Select, Spin, Typography, Table, Tag } from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { RequestTrackingResponse } from '@openapi';
import api from '@services/api';
import { formatNumber, formatTime } from '../utils/trackingUtils';

const { Option } = Select;
const { Title } = Typography;

interface DeviceSearchProps {
  onShowDetail: (request: RequestTrackingResponse) => void;
}

const DeviceSearch: React.FC<DeviceSearchProps> = ({ onShowDetail }) => {
  const [searchMac, setSearchMac] = useState<string>('');
  const [deviceRequests, setDeviceRequests] = useState<RequestTrackingResponse[]>([]);
  const [loadingDevice, setLoadingDevice] = useState<boolean>(false);
  const [searchedMac, setSearchedMac] = useState<string>('');
  const [deviceLimit, setDeviceLimit] = useState<number>(1000);
  const [devicePage, setDevicePage] = useState<number>(1);
  const [devicePageSize, setDevicePageSize] = useState<number>(10);
  const [deviceTotal, setDeviceTotal] = useState<number>(0);

  const searchDevice = async () => {
    if (!searchMac.trim()) return;

    setDevicePage(1); // Reset to first page for new search
    setLoadingDevice(true);
    try {
      const response = await api.tracking.getRequestsByDevice({
        macAddress: searchMac.trim(),
        page: 0, // Start from first page
        pageSize: devicePageSize,
        limit: deviceLimit,
      });

      if (response && response.data) {
        setDeviceRequests(response.data);
        setDeviceTotal(response.total || 0);
      }
      setSearchedMac(searchMac.trim());
    } catch (error) {
      console.error('Failed to fetch device requests:', error);
      setDeviceRequests([]);
      setDeviceTotal(0);
    } finally {
      setLoadingDevice(false);
    }
  };

  // Refetch when pagination changes
  useEffect(() => {
    if (searchedMac) {
      const fetchWithPagination = async () => {
        setLoadingDevice(true);
        try {
          const response = await api.tracking.getRequestsByDevice({
            macAddress: searchedMac,
            page: devicePage - 1,
            pageSize: devicePageSize,
            limit: deviceLimit,
          });

          if (response && response.data) {
            setDeviceRequests(response.data);
            setDeviceTotal(response.total || 0);
          }
        } catch (error) {
          console.error('Failed to fetch device requests:', error);
          setDeviceRequests([]);
          setDeviceTotal(0);
        } finally {
          setLoadingDevice(false);
        }
      };
      fetchWithPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devicePage, devicePageSize]);

  const getDeviceStatistics = () => {
    if (deviceRequests.length === 0) return null;

    const totalRequests = deviceRequests.length;
    const successCount = deviceRequests.filter((r) => (r.responseStatus || 0) < 400).length;
    const errorCount = totalRequests - successCount;
    const successRate = totalRequests > 0 ? ((successCount / totalRequests) * 100).toFixed(1) + '%' : '0%';
    const errorRate = totalRequests > 0 ? ((errorCount / totalRequests) * 100).toFixed(1) + '%' : '0%';
    const executionTimes = deviceRequests.map((r) => r.executionTimeMs || 0);
    const avgExecutionTime = executionTimes.length > 0 ? executionTimes.reduce((a, b) => a + b, 0) / totalRequests : 0;
    const maxExecutionTime = executionTimes.length > 0 ? Math.max(...executionTimes) : 0;
    const minExecutionTime = executionTimes.length > 0 ? Math.min(...executionTimes) : 0;

    return {
      totalRequests: deviceTotal, // Use total from backend
      successCount,
      errorCount,
      successRate,
      errorRate,
      avgExecutionTime,
      maxExecutionTime,
      minExecutionTime,
    };
  };

  const getDeviceChartData = () => {
    if (deviceRequests.length === 0) return { categories: [], executionTimes: [], statusCodes: [] };

    const sortedRequests = [...deviceRequests].sort((a, b) => {
      const timeA = a.requestTimestamp ? new Date(a.requestTimestamp).getTime() : 0;
      const timeB = b.requestTimestamp ? new Date(b.requestTimestamp).getTime() : 0;
      return timeA - timeB;
    });

    const categories = sortedRequests.map((r) =>
      r.requestTimestamp ? moment(r.requestTimestamp).format('MM/DD HH:mm') : '',
    );
    const executionTimes = sortedRequests.map((r) => r.executionTimeMs || 0);
    const statusCodes = sortedRequests.map((r) => r.responseStatus || 0);

    return { categories, executionTimes, statusCodes };
  };

  const deviceStats = getDeviceStatistics();
  const deviceChartData = getDeviceChartData();

  const deviceLineChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: deviceChartData.categories,
      title: {
        text: 'Request Time',
      },
      labels: {
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      title: {
        text: 'Execution Time (ms)',
      },
    },
    colors: ['#1890ff'],
    title: {
      text: 'Response Time Trend',
      align: 'left',
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val}ms`,
      },
    },
    grid: {
      borderColor: '#f1f1f1',
    },
  };

  const deviceLineSeries = [
    {
      name: 'Execution Time',
      data: deviceChartData.executionTimes,
    },
  ];

  const columns = [
    {
      title: 'MAC Address',
      dataIndex: 'macAddress',
      key: 'macAddress',
      render: (mac: string) => <Tag color="blue">{mac}</Tag>,
    },
    {
      title: 'API',
      dataIndex: 'apiVersion',
      key: 'apiVersion',
      render: (version: string) => <Tag color={version === 'v2' ? 'green' : 'orange'}>{version}</Tag>,
    },
    {
      title: 'Execution Time',
      dataIndex: 'executionTimeMs',
      key: 'executionTimeMs',
      render: (time: number) => (
        <span style={{ color: time > 5000 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>{formatTime(time)}</span>
      ),
      sorter: (a: RequestTrackingResponse, b: RequestTrackingResponse) =>
        (a.executionTimeMs || 0) - (b.executionTimeMs || 0),
    },
    {
      title: 'Status',
      dataIndex: 'responseStatus',
      key: 'responseStatus',
      render: (status: number) => <Tag color={status < 400 ? 'success' : 'error'}>{status}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'requestTimestamp',
      key: 'requestTimestamp',
      render: (timestamp: Date) => moment(timestamp).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'State',
      dataIndex: 'deviceStatus',
      key: 'deviceStatus',
      render: (status: string) => {
        if (!status) return <Tag>N/A</Tag>;
        const colorMap: Record<string, string> = {
          POSTPONED: 'orange',
          PENDING: 'blue',
          UP_TO_DATE: 'green',
          NO_STATUS: 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status.replace(/_/g, ' ')}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, record: RequestTrackingResponse) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => onShowDetail(record)} size="small">
          Details
        </Button>
      ),
    },
  ];

  return (
    <Card title="Device Statistics" style={{ marginTop: 24 }}>
      <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Input
            placeholder="Enter MAC address (e.g., AA:BB:CC:DD:EE:FF)"
            value={searchMac}
            onChange={(e) => setSearchMac(e.target.value)}
            onPressEnter={searchDevice}
            size="large"
            prefix={<UserOutlined />}
          />
        </Col>
        <Col>
          <Select value={deviceLimit} onChange={setDeviceLimit} size="large" style={{ width: 120 }}>
            <Option value={100}>100</Option>
            <Option value={500}>500</Option>
            <Option value={1000}>1,000</Option>
            <Option value={5000}>5,000</Option>
            <Option value={10000}>10,000</Option>
          </Select>
        </Col>
        <Col>
          <Button type="primary" icon={<SearchOutlined />} onClick={searchDevice} loading={loadingDevice} size="large">
            Search Device
          </Button>
        </Col>
      </Row>

      {loadingDevice && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" tip="Loading device data..." />
        </div>
      )}

      {!loadingDevice && searchedMac && deviceRequests.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
          <UserOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <div>No requests found for device: {searchedMac}</div>
        </div>
      )}

      {!loadingDevice && deviceStats && (
        <>
          <Title level={4} style={{ marginBottom: 16 }}>
            Device: {searchedMac}
          </Title>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Requests"
                  value={formatNumber(deviceStats.totalRequests)}
                  prefix={<ApiOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Success Rate"
                  value={deviceStats.successRate}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                  suffix={`(${formatNumber(deviceStats.successCount)})`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Error Rate"
                  value={deviceStats.errorRate}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                  suffix={`(${formatNumber(deviceStats.errorCount)})`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Avg Response Time"
                  value={formatTime(deviceStats.avgExecutionTime)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Response Time Trend" type="inner" style={{ marginBottom: 16 }}>
            <ReactApexChart options={deviceLineChartOptions} series={deviceLineSeries} type="line" height={350} />
          </Card>

          <Card title="Request History" type="inner">
            <Table
              dataSource={deviceRequests}
              columns={columns}
              rowKey="id"
              loading={loadingDevice}
              pagination={{
                current: devicePage,
                pageSize: devicePageSize,
                total: deviceTotal,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, newPageSize) => {
                  setDevicePage(page);
                  if (newPageSize !== devicePageSize) {
                    setDevicePageSize(newPageSize);
                    setDevicePage(1);
                  }
                },
              }}
              scroll={{ x: true }}
            />
          </Card>
        </>
      )}
    </Card>
  );
};

export default DeviceSearch;
